import {
  IconButton,
  Switch,
  TableBody,
  TableCell,
  TableRow
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import TableHead from "@saleor/components/TableHead";
import {
  VoucherBrandGroupsDetail_voucher,
  VoucherBrandGroupsDetail_voucher_voucherTenants
} from "@saleor/discounts/types/VoucherBrandGroupsDetail";
import { FormChange } from "@saleor/hooks/useForm";
import { maybe, renderCollection } from "@saleor/misc";
import React from "react";
import { FormattedMessage } from "react-intl";

import { VoucherDetails_voucher } from "../../types/VoucherDetails";
import { VoucherDetailsPageFormData } from "../VoucherDetailsPage";
export interface VoucherStoreProps {
  disabled: boolean;
  voucherStatus: boolean;
  data: VoucherDetailsPageFormData;
  stores: VoucherBrandGroupsDetail_voucher;
  voucher: VoucherDetails_voucher;
  setStoreInfo: (
    value: VoucherBrandGroupsDetail_voucher_voucherTenants
  ) => void;
  onChange: FormChange;
  onStoreAssign: () => void;
  onStoreUnassign: (id: string) => void;
  onStoreEdit: () => void;
}

const useStyles = makeStyles(
  {
    buttonDisabled: {
      "& > span": {
        color: "#8198ff"
      }
    },
    cardTitlebar: {
      alignItems: "center",
      display: "flex"
    },
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 100
    },
    colActions2: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 10
    },
    colActions3: {
      padding: 0
    },
    colCheckbox: {
      paddingLeft: "12px !important"
    },
    colClassification: {
      width: 200
    },
    colDiscount: {
      textAlign: "center",
      width: 100
    },
    colEmpty: {
      width: "1px"
    },
    colName: {
      paddingLeft: 0,
      width: 300
    },
    emptyMessageHeader: {
      fontSize: "20px",
      marginBottom: "7px",
      marginTop: "30px",
      textAlign: "center"
    },
    emptyMessageText: {
      marginBottom: "30px",
      textAlign: "center"
    },
    switchDisabled: {
      "& .MuiSwitch-colorPrimary.Mui-disabled": {
        color: "#ffff"
      },
      "& .MuiSwitch-colorPrimary.Mui-disabled + .MuiSwitch-track": {
        backgroundColor: "#C8C8C8"
      }
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  },
  { name: "DiscountProducts" }
);

const numberOfColumns = 6;

const VoucherStore: React.FC<VoucherStoreProps> = props => {
  const {
    data,
    disabled,
    voucherStatus,
    stores,
    onChange,
    onStoreAssign,
    onStoreUnassign,
    onStoreEdit,
    setStoreInfo
  } = props;
  const classes = useStyles(props);
  const voucherTenants = maybe(() => stores?.voucherTenants);
  const [selectedTenants, setSelectedTenants] = React.useState([]);
  const disableConditions =
    disabled ||
    voucherStatus ||
    !data.isEligibleStores ||
    data?.type !== "SPECIFIC_PRODUCT";

  React.useEffect(() => {
    if (
      (stores?.voucherTenants?.length > 0 && !disabled) ||
      data.isEligibleStores
    ) {
      onChange({
        target: {
          name: "isEligibleStores",
          value: true
        }
      });
    }
  }, [stores, data.isEligibleStores]);

  const handleChecked = event => {
    const { value } = event.target;
    if (selectedTenants.includes(value)) {
      setSelectedTenants(
        selectedTenants.filter(tenantId => tenantId !== value)
      );
    } else {
      setSelectedTenants([...selectedTenants, value]);
    }
  };

  const toggleAll = (tenants, selected) => {
    if (selected) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(tenants.map(tenant => tenant.id));
    }
  };

  const handleEligibleStoreEdit = tenant => {
    setStoreInfo(tenant);
    onStoreEdit();
  };
  return (
    <Card>
      <CardTitle
        title={
          <div className={classes.cardTitlebar}>
            <Switch
              className={voucherStatus ? classes.switchDisabled : null}
              id="isEligibleStoreToggle"
              disabled={
                voucherStatus ||
                disabled ||
                data.isEligibleItems ||
                data?.type !== "SPECIFIC_PRODUCT"
              }
              color="primary"
              checked={data.isEligibleStores}
              onChange={event =>
                onChange({
                  target: {
                    name: "isEligibleStores",
                    value: event.target.checked
                  }
                })
              }
              name="isEligibleStores"
            />
            <div>{`Eligible Stores (${maybe(
              () => stores.voucherTenants.length,
              0
            )})`}</div>
          </div>
        }
        toolbar={
          <Button
            id="assignStoresBtn"
            disabled={disableConditions}
            color="primary"
            className={disableConditions ? classes.buttonDisabled : null}
            onClick={onStoreAssign}
          >
            <FormattedMessage
              defaultMessage="Assign stores"
              description="button"
            />
          </Button>
        }
      />
      {data.isEligibleStores ? (
        <>
          {!voucherTenants.length ? (
            <>
              <div className={classes.emptyMessageHeader}>
                <FormattedMessage defaultMessage="No stores assigned at the moment." />
              </div>
              <div className={classes.emptyMessageText}>
                <FormattedMessage defaultMessage="Vouchers will be applied  in store level." />
              </div>
            </>
          ) : null}
          {voucherTenants.length ? (
            <ResponsiveTable className={classes.table}>
              <colgroup>
                <col />
                <col className={classes.colName} />
                <col className={classes.colClassification} />
                <col className={classes.colDiscount} />
                <col className={classes.colDiscount} />
                <col className={classes.colActions} />
              </colgroup>
              <TableHead
                items={voucherTenants.map(tenant => tenant)}
                colSpan={numberOfColumns}
                disabled={false}
                toggleAll={toggleAll}
                selected={selectedTenants.length}
              >
                <TableCell className={classes.colName}>Store Name</TableCell>
                <TableCell className={classes.colClassification}>
                  Classification
                </TableCell>
                <TableCell className={classes.colDiscount}>
                  Tenant Discount
                </TableCell>
                <TableCell className={classes.colDiscount}>
                  SMOP Discount
                </TableCell>
                <TableCell className={classes.colActions}>Action</TableCell>
              </TableHead>
              <TableBody>
                {renderCollection(voucherTenants, (tenant, index) => (
                  <TableRow className={classes.tableRow}>
                    <TableCell className={classes.colCheckbox}>
                      <Checkbox
                        id={`checkboxRow${index}`}
                        name="all"
                        checked={selectedTenants.includes(tenant.id)}
                        value={tenant.id}
                        onChange={handleChecked}
                      />
                    </TableCell>
                    <TableCell
                      id={`storeNameRow${index}`}
                      className={classes.colName}
                    >{`${tenant.tenant.brand.name} - ${tenant.tenant.brand.legalName} - ${tenant.tenant.mall.name}`}</TableCell>
                    <TableCell
                      className={classes.colClassification}
                      id={`classificationRow${index}`}
                    >
                      {tenant.tenant.tenantType}
                    </TableCell>
                    <TableCell
                      className={classes.colDiscount}
                      id={`tenantDiscountRow${index}`}
                    >
                      {tenant.tenantDiscountShare}%
                    </TableCell>
                    <TableCell
                      className={classes.colDiscount}
                      id={`smopDiscountRow${index}`}
                    >
                      {tenant.smoDiscountShare}%
                    </TableCell>
                    <TableCell
                      id={`storeEditBtn${index}`}
                      className={classes.colActions2}
                    >
                      <div className={classes.colActions3}>
                        <IconButton
                          color="primary"
                          onClick={() => handleEligibleStoreEdit(tenant)}
                          disabled={voucherStatus}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => onStoreUnassign(tenant.id)}
                          disabled={voucherStatus}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          ) : null}
        </>
      ) : null}
    </Card>
  );
};

VoucherStore.displayName = "VoucherStore";
export default VoucherStore;
